import React from 'react';
import { ArrowLeft, Check, Delete, LoaderCircle, ShieldCheck } from 'lucide-react';
import { Button } from '../ui/button';

export const PIZZUL_POSES = {
  happy: '/pizzul/happy.png',
  thinking: '/pizzul/thinking.png',
  confused: '/pizzul/confused.png',
  construction: '/pizzul/construction.png',
  satisfied: '/pizzul/satisfied.png',
  chill: '/pizzul/chill.png',
  writing: '/pizzul/writing.png',
};

export const ReflectShell = ({
  children,
  onBack,
  eyebrow = 'STUDIO PLAYGROUND',
  compact = false,
}) => (
  <main className="reflect-page min-h-screen overflow-x-hidden bg-[#050607] text-white">
    <div className="reflect-orb reflect-orb-one" />
    <div className="reflect-orb reflect-orb-two" />
    <div className={`relative z-10 mx-auto flex min-h-screen w-full max-w-6xl flex-col
      px-4 py-5 sm:px-7 ${compact ? 'lg:py-7' : 'lg:py-10'}`}
    >
      <header className="mb-6 flex min-h-11 items-center justify-between gap-3 print:hidden">
        <div className="flex min-w-11 items-center">
          {onBack && (
            <Button
              type="button"
              variant="ghost"
              onClick={onBack}
              className="min-h-11 min-w-11 rounded-full text-base text-white hover:bg-white/10
                focus-visible:ring-2 focus-visible:ring-cyan"
              aria-label="Go back"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
          )}
        </div>
        <p className="font-heading text-sm font-black tracking-[0.24em] text-cyan sm:text-base">
          {eyebrow}
        </p>
        <div className="min-w-11" />
      </header>
      {children}
    </div>
  </main>
);

export const Pizzul = ({ pose = 'happy', alt = 'Pizzul', className = '' }) => (
  <img
    src={PIZZUL_POSES[pose]}
    alt={alt}
    className={`select-none object-contain drop-shadow-[0_0_24px_rgba(0,255,221,0.16)] ${className}`}
  />
);

export const StepHeading = ({ kicker, title, children }) => (
  <div className="mx-auto mb-7 max-w-3xl text-center">
    {kicker && (
      <p className="mb-2 text-sm font-extrabold uppercase tracking-[0.18em] text-cyan">
        {kicker}
      </p>
    )}
    <h1 className="font-heading text-3xl font-black leading-tight sm:text-5xl">{title}</h1>
    {children && (
      <div className="mt-3 text-base leading-relaxed text-white/65 sm:text-lg">{children}</div>
    )}
  </div>
);

export const ErrorMessage = ({ children }) => children ? (
  <div
    role="alert"
    className="mt-4 rounded-2xl border border-[#FF7A59]/40 bg-[#FF7A59]/10
      px-4 py-3 text-base font-bold text-[#FFB19C]"
  >
    {children}
  </div>
) : null;

export const LoadingScreen = ({ line = 'Getting your studio ready...' }) => (
  <ReflectShell>
    <div className="flex flex-1 flex-col items-center justify-center pb-24 text-center">
      <Pizzul pose="thinking" className="h-40 w-40 sm:h-52 sm:w-52" />
      <LoaderCircle className="mt-2 h-8 w-8 animate-spin text-cyan" aria-hidden="true" />
      <p className="mt-4 text-lg font-bold text-white/75">{line}</p>
    </div>
  </ReflectShell>
);

const KEYS = ['1', '2', '3', '4', '5', '6', '7', '8', '9', 'clear', '0', 'delete'];

const PIN_INPUT_CLASSES = [
  'mb-4 h-16 w-full rounded-2xl border border-white/15 bg-white/[0.06] px-4 text-center',
  'font-heading text-3xl font-black tracking-[0.55em] text-cyan outline-none',
  'focus:border-cyan focus:ring-2 focus:ring-cyan/40',
].join(' ');

const KEY_BUTTON_CLASSES = [
  'flex min-h-14 items-center justify-center rounded-2xl border border-white/10',
  'bg-white/[0.06] text-xl font-black transition hover:border-cyan/60 hover:bg-cyan/10',
  'focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan disabled:opacity-40',
].join(' ');

export const PinKeypad = ({
  value,
  onChange,
  onSubmit,
  label = 'Your 4-digit PIN',
  disabled = false,
}) => {
  const update = (key) => {
    if (disabled) return;
    if (key === 'clear') onChange('');
    else if (key === 'delete') onChange(value.slice(0, -1));
    else if (value.length < 4) onChange(`${value}${key}`);
  };

  return (
    <div className="mx-auto w-full max-w-xs">
      <label
        htmlFor="reflect-pin"
        className="mb-3 block text-center text-base font-bold text-white/75"
      >
        {label}
      </label>
      <input
        id="reflect-pin"
        value={value}
        onChange={(event) => onChange(event.target.value.replace(/\D/g, '').slice(0, 4))}
        type="password"
        inputMode="numeric"
        autoComplete="off"
        autoFocus
        disabled={disabled}
        onKeyDown={(event) => {
          if (event.key === 'Enter' && value.length === 4 && onSubmit) {
            event.preventDefault();
            onSubmit();
          }
        }}
        className={PIN_INPUT_CLASSES}
        aria-describedby="pin-help"
      />
      <p id="pin-help" className="sr-only">
        Enter four numbers using your keyboard or the keypad.
      </p>
      <div className="grid grid-cols-3 gap-2">
        {KEYS.map((key) => (
          <button
            key={key}
            type="button"
            onClick={() => update(key)}
            disabled={disabled}
            className={KEY_BUTTON_CLASSES}
            aria-label={key === 'delete'
              ? 'Delete last digit'
              : key === 'clear' ? 'Clear PIN' : `Number ${key}`}
          >
            {key === 'delete' ? <Delete className="h-5 w-5" /> : key === 'clear' ? 'Clear' : key}
          </button>
        ))}
      </div>
    </div>
  );
};

export const PickedStudent = ({ displayName, onPickAgain }) => (
  <div className="mb-4 text-center">
    <p className="text-base font-extrabold text-cyan">Hi, {displayName}</p>
    <button
      type="button"
      onClick={onPickAgain}
      className="min-h-11 text-base font-bold text-white/60 underline decoration-white/30 underline-offset-4
        hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan"
    >
      Not you? Pick again
    </button>
  </div>
);

export const SavedMark = ({ saving, saved }) => (
  <span
    className="inline-flex min-h-6 items-center gap-1 text-sm font-bold text-white/45"
    aria-live="polite"
  >
    {saving && <><LoaderCircle className="h-4 w-4 animate-spin" /> Saving</>}
    {!saving && saved && <><Check className="h-4 w-4 text-cyan" /> Saved</>}
  </span>
);

export const CareMessage = ({ canContinue, onContinue }) => (
  <div
    className="fixed inset-0 z-50 flex items-center justify-center bg-black/85 p-4"
    role="dialog"
    aria-modal="true"
    aria-labelledby="care-title"
  >
    <div className="w-full max-w-xl rounded-[2rem] border border-cyan/40 bg-[#101315]
      p-6 text-center shadow-[0_0_50px_rgba(0,255,221,0.12)] sm:p-9"
    >
      <ShieldCheck className="mx-auto h-10 w-10 text-cyan" />
      <h2 id="care-title" className="mt-4 font-heading text-2xl font-black sm:text-3xl">
        You deserve support right now.
      </h2>
      <p className="mt-4 text-lg leading-relaxed text-white/80">
        It sounds like something serious is going on. Please talk to your teacher or another
        adult you trust right now. You can also call or text 988.
      </p>
      <Button
        type="button"
        onClick={onContinue}
        disabled={!canContinue}
        className="mt-7 min-h-12 w-full rounded-full bg-cyan px-7 text-base font-black text-black
          hover:bg-cyan/90 focus-visible:ring-2 focus-visible:ring-white"
      >
        {canContinue ? 'Continue' : 'Stay here for a moment'}
      </Button>
    </div>
  </div>
);
